import { Button, Flex, Result } from 'antd'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useSessionContext } from 'src/hooks'
import { useWorkspaceMenu } from 'src/layout/AppLayoutShell/hooks'
import { paths } from 'src/router/paths'
import type { WorkspaceAccessStatus } from 'src/utils/workspaceMenuAccess'

type Props = {
  status: Exclude<WorkspaceAccessStatus, 'ok'>
  workspaceId: string
}

export const WorkspaceAccessDenied = ({ status, workspaceId }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { removeUserData, user } = useSessionContext()

  // The sidebar model is already filtered by the member's permissions and by
  // the feature flags, so its first entry is a section we know they can open.
  // Sending them to a hardcoded page (Overview) was the bug: Overview itself
  // needs `organization.view`, so a member without it landed back on this very
  // screen and the button looked dead.
  const { groups } = useWorkspaceMenu({
    workspaceId: Number(workspaceId),
    user
  })
  const firstAllowedSection = groups.flatMap((group) => group.items)[0]

  const content =
    status === 'no-access' ? (
      <Result
        status={403}
        title={t('workspaces.accessDenied.noAccess.title')}
        subTitle={t('workspaces.accessDenied.noAccess.description')}
        extra={[
          <Button
            key="switch"
            type="primary"
            onClick={() =>
              removeUserData({
                redirectTo: `${location.pathname}${location.search}`
              })
            }
          >
            {t('workspaces.accessDenied.noAccess.switchAccount')}
          </Button>,
          <Button
            key="workspaces"
            onClick={() => navigate(paths.workspaces.root)}
          >
            {t('workspaces.accessDenied.noAccess.backToWorkspaces')}
          </Button>
        ]}
      />
    ) : (
      <Result
        status={403}
        title={t('workspaces.accessDenied.insufficientRole.title')}
        subTitle={t('workspaces.accessDenied.insufficientRole.description')}
        extra={[
          // Only offered when there is somewhere to go. A member with no
          // permissions at all has no reachable section, and the workspace
          // list below is their only way out.
          ...(firstAllowedSection
            ? [
                <Button
                  key="section"
                  type="primary"
                  onClick={() => navigate(firstAllowedSection.path)}
                >
                  {t('workspaces.accessDenied.insufficientRole.goToSection', {
                    section: firstAllowedSection.label
                  })}
                </Button>
              ]
            : []),
          <Button
            key="workspaces"
            type={firstAllowedSection ? 'default' : 'primary'}
            onClick={() => navigate(paths.workspaces.root)}
          >
            {t('workspaces.accessDenied.noAccess.backToWorkspaces')}
          </Button>
        ]}
      />
    )

  return (
    <Flex justify="center" align="center" className="h-full">
      {content}
    </Flex>
  )
}
